import { useEffect, useState } from 'react';
import { ViewportData } from 'ais-facade-api/api';
import { useGetTargetsMutation } from 'app/api/facade';
import { svgToDataURL } from 'app/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { setAtonsOnMap } from 'app/utils/facadeReducer';
import { setHoveredTarget } from 'app/utils/mapStateSlice';
import { setSelectedTarget } from 'app/utils/selectedTargetsSlice';
import { IconLayer } from 'deck.gl/typed';
import { getVesselIcon } from 'helpers/getWebGLPointIcon';

export const useLayerAtons = (showIndicator: boolean, showAtons: boolean) => {
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isNotBaseMode = mode === 'analytics' || mode === 'playback';
  const dispatch = useAppDispatch();
  const atons = useAppSelector((state) => state.facadeSlice.atonsOnMap);
  const [, { data: atonsResponse, ...atonsRequest }] = useGetTargetsMutation({
    fixedCacheKey: 'get-map-atons',
  });

  const [isBlinkingVisible, setBlinkingVisible] = useState(true);
  const getBlinkingSafetyIcon = (isVisible: boolean) =>
    `<svg class="safety-icon" width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
  <rect width="28" height="28" fill="none"/>
  <g transform="translate(7, 7)">
    <path d="M7 0.5C3.42386 0.5 0.5 3.42386 0.5 7C0.5 10.5761 3.42386 13.5 7 13.5C10.5761 13.5 13.5 10.5761 13.5 7C13.5 3.42386 10.5761 0.5 7 0.5ZM7 3.5C7.47978 3.5 7.97556 3.62807 8.45726 3.83563L7 5.29289L5.54274 3.83563C6.02444 3.62807 6.52022 3.5 7 3.5ZM3.5 7C3.5 6.52022 3.62807 6.02444 3.83563 5.54274L5.29289 7L3.83563 8.45726C3.62807 7.97556 3.5 7.47978 3.5 7ZM7 10.5C6.52022 10.5 6.02444 10.3719 5.54274 10.1644L7 8.70711L8.45726 10.1644C7.97556 10.3719 7.47978 10.5 7 10.5ZM10.5 7C10.5 7.47978 10.3719 7.97556 10.1644 8.45726L8.70711 7L10.1644 5.54274C10.3719 6.02444 10.5 6.52022 10.5 7Z" 
    fill="${isVisible ? '#FF0000' : 'none'}" stroke="white"/>
  </g>
    </svg>`;

  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkingVisible((prev) => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (atonsResponse?.results && atonsRequest.isSuccess) {
      dispatch(setAtonsOnMap(atonsResponse?.results));
    }
  }, [atonsRequest.isSuccess]);

  const layerAtons = new IconLayer({
    id: 'layer-atons',
    data: atons,
    pickable: true,
    getPosition: (d: ViewportData): [number, number, number] => {
      if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
      return [999, 999, 10];
    },
    getIcon: (d) => {
      if ('safety' in d && typeof d?.safety?.lastSignal === 'string') {
        return {
          url: svgToDataURL(getBlinkingSafetyIcon(isBlinkingVisible)),
          width: 28,
          height: 28,
        };
      }
      return {
        url: svgToDataURL(getVesselIcon(d, showIndicator)),
        width: 32,
        height: 32,
      };
    },
    updateTriggers: {
      getIcon: [isBlinkingVisible],
    },
    onClick(pickingInfo) {
      dispatch(setSelectedTarget({ target: pickingInfo, type: 'aton' }));
    },
    onHover(pickingInfo) {
      dispatch(setHoveredTarget(pickingInfo));
    },
    sizeMinPixels: 32,
    autoHighlight: true,
    visible: showAtons && !isNotBaseMode,
  });
  return layerAtons;
};
